import Phaser from 'phaser';

const SKY_TOP = 0x1e1b4b;
const SKY_MID = 0x7c2d12;
const SKY_BOT = 0xdc2626;
const LIGHTNING = 0xfde047;
const CITY_DEEP = 0x020617;
const CITY_MID = 0x1e293b;
const ZORD_BODY = 0xe5e7eb;
const ZORD_ACCENT = 0xef4444;
const ZORD_GOLD = 0xfde047;
const WINDOW = 0xfbbf24;

export function drawZordBattleSkyBackground(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
) {
  g.clear();
  const step = Math.max(1, Math.floor(h / 48));
  for (let y = 0; y < h; y += step) {
    const t = y / h;
    let r: number, gC: number, b: number;
    if (t < 0.5) {
      const u = t / 0.5;
      r = lerp((SKY_TOP >> 16) & 0xff, (SKY_MID >> 16) & 0xff, u);
      gC = lerp((SKY_TOP >> 8) & 0xff, (SKY_MID >> 8) & 0xff, u);
      b = lerp(SKY_TOP & 0xff, SKY_MID & 0xff, u);
    } else {
      const u = (t - 0.5) / 0.5;
      r = lerp((SKY_MID >> 16) & 0xff, (SKY_BOT >> 16) & 0xff, u);
      gC = lerp((SKY_MID >> 8) & 0xff, (SKY_BOT >> 8) & 0xff, u);
      b = lerp(SKY_MID & 0xff, SKY_BOT & 0xff, u);
    }
    g.fillStyle((r << 16) | (gC << 8) | b, 1);
    g.fillRect(0, y, w, step + 1);
  }

  g.fillStyle(0x0a0612, 0.3);
  for (let i = 0; i < 5; i++) {
    const cx = (i / 5) * w + 40;
    const cy = Math.floor(h * 0.2) + (i % 2) * 20;
    g.fillEllipse(cx, cy, 200, 50);
  }

  g.fillStyle(LIGHTNING, 0.9);
  const boltX = Math.floor(w * 0.3);
  const boltY = Math.floor(h * 0.1);
  const boltPts = [
    { x: 0, y: 0 },
    { x: -8, y: 40 },
    { x: 4, y: 40 },
    { x: -14, y: 100 },
    { x: 2, y: 55 },
    { x: -4, y: 55 },
    { x: 10, y: 15 },
  ];
  g.beginPath();
  g.moveTo(boltX + boltPts[0].x, boltY + boltPts[0].y);
  for (let i = 1; i < boltPts.length; i++) {
    g.lineTo(boltX + boltPts[i].x, boltY + boltPts[i].y);
  }
  g.closePath();
  g.fillPath();

  g.fillStyle(LIGHTNING, 0.5);
  g.fillCircle(boltX + 2, boltY + 30, 8);
  g.fillCircle(boltX - 2, boltY + 80, 10);
}

export function drawZordBattleSilhouette(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
) {
  const baselineY = Math.floor(h * 0.82);

  g.fillStyle(CITY_DEEP, 1);
  g.fillRect(0, baselineY, w, h - baselineY);

  g.fillStyle(CITY_MID, 1);
  let x = 0;
  let seed = 17;
  const rand = () => {
    seed = (seed + 0x6d2b79f5) >>> 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  while (x < w) {
    const bw = 16 + Math.floor(rand() * 34);
    const bh = 40 + Math.floor(rand() * 80);
    g.fillRect(x, baselineY - bh, bw, bh);
    for (let r = 0; r < Math.floor(bh / 12); r++) {
      for (let c = 0; c < Math.floor(bw / 6); c++) {
        if (rand() > 0.7) continue;
        g.fillStyle(WINDOW, 0.8);
        g.fillRect(x + 2 + c * 6, baselineY - bh + 4 + r * 12, 2, 3);
      }
    }
    g.fillStyle(CITY_MID, 1);
    x += bw + Math.floor(rand() * 4);
  }

  const zx = Math.floor(w * 0.55);
  const zy = baselineY - 30;
  const scale = 1.4;

  g.fillStyle(ZORD_BODY, 1);
  g.fillRect(zx - 40 * scale, zy - 60 * scale, 80 * scale, 70 * scale);
  g.fillStyle(ZORD_ACCENT, 1);
  g.fillRect(zx - 40 * scale, zy - 60 * scale, 80 * scale, 15 * scale);

  g.fillStyle(ZORD_BODY, 1);
  g.fillRect(zx - 25 * scale, zy - 90 * scale, 50 * scale, 30 * scale);
  g.fillStyle(ZORD_GOLD, 1);
  g.fillRect(zx - 20 * scale, zy - 82 * scale, 40 * scale, 5 * scale);
  g.fillStyle(ZORD_ACCENT, 1);
  g.fillRect(zx - 8 * scale, zy - 75 * scale, 6 * scale, 4 * scale);
  g.fillRect(zx + 2 * scale, zy - 75 * scale, 6 * scale, 4 * scale);
  g.fillStyle(0xffffff, 1);
  g.fillRect(zx - 6 * scale, zy - 74 * scale, 2 * scale, 2 * scale);
  g.fillRect(zx + 4 * scale, zy - 74 * scale, 2 * scale, 2 * scale);

  g.fillStyle(ZORD_BODY, 1);
  g.fillRect(zx - 60 * scale, zy - 50 * scale, 20 * scale, 45 * scale);
  g.fillRect(zx + 40 * scale, zy - 50 * scale, 20 * scale, 45 * scale);
  g.fillStyle(ZORD_ACCENT, 1);
  g.fillRect(zx - 58 * scale, zy - 30 * scale, 16 * scale, 6 * scale);
  g.fillRect(zx + 42 * scale, zy - 30 * scale, 16 * scale, 6 * scale);

  g.fillStyle(ZORD_BODY, 1);
  g.fillRect(zx - 30 * scale, zy + 10 * scale, 25 * scale, 20 * scale);
  g.fillRect(zx + 5 * scale, zy + 10 * scale, 25 * scale, 20 * scale);

  g.fillStyle(ZORD_GOLD, 0.7);
  g.fillCircle(zx, zy - 40 * scale, 14 * scale);
  g.fillStyle(ZORD_ACCENT, 1);
  g.fillTriangle(zx - 10 * scale, zy - 45 * scale, zx, zy - 28 * scale, zx + 10 * scale, zy - 45 * scale);
}

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}
